import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, ref, Ref, onMounted } from "vue"
import type { ComponentPublicInstance } from "vue"
import moment from "moment"

// Utils
import { isScreenMD } from "@/vue/planning/utils/breakpoints"
import formatDateString from "@/vue/planning/utils/formatDate"

// Components
import FlexiblePaymentConditionPill from "./FlexiblePaymentConditionPill.vue"
import PlaceholderBox from "@/vue/planning/components/common/placeholderBox.vue"
import PackageDetailsTooltip from "./PackageDetailsTooltip.vue"
import DynamicPopup from "@/vue/common/DynamicPopup.vue"
import PaymentConditionPopup from "./PaymentConditionPopup.vue"
import TermsAndConditions from "./TermsAndConditions.vue"

// Composables
import useLocale from "@/vue/composables/useLocale"

interface Props {
	displayTitle: boolean
	content: { [key: string]: string }
	isPVFlex?: boolean
	prestId?: string
	isDisplayPlaceholders?: boolean
	cancellationDate?: string
	startDate?: string
}

type DynamicPopupInstance = ComponentPublicInstance<typeof DynamicPopup>


export default /*#__PURE__*/_defineComponent({
  __name: 'FlexiblePaymentCondition',
  props: {
    displayTitle: { type: Boolean },
    content: null,
    isPVFlex: { type: Boolean },
    prestId: null,
    isDisplayPlaceholders: { type: Boolean },
    cancellationDate: null,
    startDate: null
  },
  setup(__props: any) {

const props = __props as {
	displayTitle: boolean
	content: { [key: string]: string }
	isPVFlex?: boolean
	prestId?: string
	isDisplayPlaceholders?: boolean
	cancellationDate?: string
	startDate?: string
};

const locale = useLocale()

// Props


// Refs
const popupRef: Ref<DynamicPopupInstance | null> = ref(null)

// Computeds
const isStartDateMoreThen40Days: ComputedRef<boolean> = computed(() => {
	if (!props.startDate) {
		return false // Handle cases where startDate might not be provided
	}


	const startDate = moment(props.startDate, 'DD/MM/YYYY', true)
	const today = moment()

	if (!startDate.isValid()) {
		console.error("Invalid date format for startDate: ", props.startDate);
		return false;
	}

	// Calculate the difference in days
	const diffDays = startDate.diff(today, 'days')
	return diffDays > 40
})

const content: ComputedRef<{ [key: string]: string }> = computed(() => {
	return props.content
})

const formattedCancellationDate: ComputedRef<string> = computed(() => {
	return props.cancellationDate ? formatDateString(props.cancellationDate) : ""
})

const baseTitle: ComputedRef<string> = computed(() => {
	const base = props.isPVFlex
		? content.value.flex_line_1_PV
		: content.value.flex_line_1
		
	return `${base} ${formattedCancellationDate.value}${content.value.flex_line_2 || ''}`
})

const title: ComputedRef<string> = computed(() => {
	return baseTitle.value
})

// Methods
const getPVCancellationPolicy = (): string => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return `${
		content.value.flex_picto_PV_cancelation_until ?? ""
	} 
	${cancellationDateFormatted}
		
	${
		content.value.flex_picto_PV_i_terms_conditions_1 ?? ""
	} 
	`
}

const getPVFirstSentence = (): string => {
	return isStartDateMoreThen40Days.value
		? content.value.flex_picto_PV_i_payment_condition_before_deposit
		: content.value.flex_picto_PV_i_payment_condition_after_deposit
}

const getDefaultSentences = (): string[] => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return [
		`${content.value.flex_picto_i_1 ?? ""} ${locale !== "nl_NL" ? cancellationDateFormatted: ''} ${
			content.value.flex_picto_i_1_2 ?? ""
		}`,
		`${
			content.value.flex_picto_i_terms_conditions_1 ?? ""
		}`,
	]
}

// Computed property using helper methods with TypeScript type
const sentences: ComputedRef<string[]> = computed(() => {
	if (props.isPVFlex) {
		return [getPVFirstSentence(), getPVCancellationPolicy()]
	} else {
		return getDefaultSentences()
	}
})

// Methods
const handleOpenPopup = (): void => {
	// Only open the popup if the device is mobile
	if (isScreenMD.value) return

	if (popupRef.value) {
		popupRef.value.openPopup()
	}
}

const handleClosePopUp = () => {
	if (popupRef.value) {
		popupRef.value.closePopup()
	}
}


return { __sfc: true,locale, props, popupRef, isStartDateMoreThen40Days, content, formattedCancellationDate, baseTitle, title, getPVCancellationPolicy, getPVFirstSentence, getDefaultSentences, sentences, handleOpenPopup, handleClosePopUp, FlexiblePaymentConditionPill, PlaceholderBox, PackageDetailsTooltip, DynamicPopup, PaymentConditionPopup, TermsAndConditions }
}

})